// src/utils/promotionUtils.js

/**
 * Promotion utility functions for data formatting, validation, and transformation
 */

/**
 * Format promotion data for API submission
 * @param {Object} formData - Raw form data
 * @returns {Object} - Formatted data for API
 */
export const formatPromotionForAPI = (formData) => {
  try {
    console.log('🔄 Formatting promotion data for API:', formData)

    const formatted = {
      name: formData.name?.trim(),
      description: formData.description?.trim() || '',
      promotionType: formData.promotionType,
      discountType: formData.discountType,
      discountValue: parseFloat(formData.discountValue),
      priority: parseInt(formData.priority) || 1,
      targetAudience: formData.targetAudience,
      startDate: formatDateForAPI(formData.startDate),
      endDate: formatDateForAPI(formData.endDate),
      isActive: formData.isActive !== undefined ? formData.isActive : true
    }

    // Add products if provided (for product_based promotions)
    if (formData.products && Array.isArray(formData.products)) {
      formatted.products = formData.products.filter(Boolean) // Remove empty values
    }

    // Format conditions if provided
    if (formData.conditions && Array.isArray(formData.conditions)) {
      formatted.conditions = formData.conditions
        .filter(condition => condition && condition.type) // Remove empty conditions
        .map(formatConditionForAPI)
    }

    console.log('✅ Formatted promotion data:', formatted)
    return formatted
  } catch (error) {
    console.error('❌ Error formatting promotion data:', error)
    throw new Error('Failed to format promotion data')
  }
}

/**
 * Format condition data for API
 * @param {Object} condition - Raw condition data
 * @returns {Object} - Formatted condition
 */
export const formatConditionForAPI = (condition) => {
  const formatted = {
    type: condition.type
  }

  switch (condition.type) {
    case 'days_active':
      formatted.daysActive = Array.isArray(condition.daysActive) 
        ? condition.daysActive.filter(Boolean)
        : condition.value?.split(',').map(day => day.trim().toLowerCase()).filter(Boolean) || []
      break

    case 'time_range':
      if (condition.timeRange) {
        formatted.timeRange = {
          startTime: condition.timeRange.startTime,
          endTime: condition.timeRange.endTime
        }
      } else if (condition.value) {
        // Handle case where time range is provided as a string "HH:MM - HH:MM"
        const [startTime, endTime] = condition.value.split(' - ').map(time => time.trim())
        formatted.timeRange = { startTime, endTime }
      }
      break

    case 'min_quantity':
      formatted.minQuantity = parseInt(condition.minQuantity) || parseInt(condition.value) || 1
      break

    case 'max_uses_per_order':
      formatted.maxUsesPerOrder = parseInt(condition.maxUsesPerOrder) || parseInt(condition.value) || 1
      break

    case 'min_order_value':
      formatted.minOrderValue = parseFloat(condition.minOrderValue) || parseFloat(condition.value) || 0
      break

    case 'payment_method':
      formatted.paymentMethods = Array.isArray(condition.paymentMethods)
        ? condition.paymentMethods.filter(Boolean)
        : condition.value?.split(',').map(method => method.trim().toLowerCase()).filter(Boolean) || []
      break

    case 'first_order':
      formatted.isFirstOrder = Boolean(condition.isFirstOrder || condition.value)
      break

    default:
      // For any custom conditions, just pass through the value
      if (condition.value !== undefined) {
        formatted.value = condition.value
      }
  }

  return formatted
}

/**
 * Format date for API (ISO string)
 * @param {string|Date} date - Date to format
 * @returns {string} - ISO date string
 */
export const formatDateForAPI = (date) => {
  if (!date) return null
  
  try {
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) {
      throw new Error('Invalid date')
    }
    return dateObj.toISOString()
  } catch (error) {
    console.error('❌ Error formatting date:', error)
    return null
  }
}

/**
 * Format promotion data received from API for display
 * @param {Object} apiData - Raw API response data
 * @returns {Object} - Formatted data for UI
 */
export const formatPromotionFromAPI = (apiData) => {
  try {
    console.log('🔄 Formatting promotion data from API:', apiData)

    const promotion = apiData.promotion || apiData

    const formatted = {
      ...promotion,
      startDateFormatted: formatDateForDisplay(promotion.startDate),
      endDateFormatted: formatDateForDisplay(promotion.endDate),
      discountValueFormatted: formatDiscountValue(promotion.discountValue, promotion.discountType),
      priorityText: getPriorityText(promotion.priority),
      statusText: promotion.isActive ? 'Active' : 'Inactive',
      typeText: getPromotionTypeText(promotion.promotionType),
      conditionsFormatted: formatConditionsForDisplay(promotion.conditions),
      productsCount: promotion.products ? promotion.products.length : 0,
      targetAudienceName: getTargetAudienceName(promotion.targetAudience)
    }

    console.log('✅ Formatted promotion data for display:', formatted)
    return formatted
  } catch (error) {
    console.error('❌ Error formatting promotion data from API:', error)
    return apiData // Return original data if formatting fails
  }
}

/**
 * Format date for display (human readable)
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date string
 */
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'N/A'
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid Date'
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch (error) {
    console.error('❌ Error formatting date for display:', error)
    return 'Invalid Date'
  }
}

/**
 * Format discount value for display
 * @param {number} value - Discount value
 * @param {string} type - Discount type (fixed/percentage)
 * @returns {string} - Formatted discount value
 */
export const formatDiscountValue = (value, type) => {
  if (value === undefined || value === null) return 'N/A'
  
  try {
    if (type === 'percentage') {
      return `${value}%`
    } else if (type === 'fixed') {
      return `${value.toFixed(2)}`
    }
    return value.toString()
  } catch (error) {
    console.error('❌ Error formatting discount value:', error)
    return value?.toString() || 'N/A'
  }
}

/**
 * Get priority text representation
 * @param {number} priority - Priority number (1-4)
 * @returns {string} - Priority text
 */
export const getPriorityText = (priority) => {
  const priorities = {
    1: 'Highest',
    2: 'High',
    3: 'Medium',
    4: 'Low'
  }
  return priorities[priority] || 'Unknown'
}

/**
 * Get promotion type text representation
 * @param {string} type - Promotion type
 * @returns {string} - Formatted type text
 */
export const getPromotionTypeText = (type) => {
  const types = {
    'product_based': 'Product Based',
    'order_based': 'Order Based',
    'customer_based': 'Customer Based',
    'quantity_based': 'Quantity Based'
  }
  return types[type] || type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'
}

/**
 * Format conditions for display
 * @param {Array} conditions - Array of conditions
 * @returns {Array} - Formatted conditions
 */
export const formatConditionsForDisplay = (conditions) => {
  if (!Array.isArray(conditions)) return []
  
  return conditions.map(condition => ({
    ...condition,
    displayText: getConditionDisplayText(condition),
    typeText: getConditionTypeText(condition.type)
  }))
}

/**
 * Get condition display text
 * @param {Object} condition - Condition object
 * @returns {string} - Human readable condition text
 */
export const getConditionDisplayText = (condition) => {
  try {
    switch (condition.type) {
      case 'days_active':
        return `Active on: ${condition.daysActive?.map(day => 
          day.charAt(0).toUpperCase() + day.slice(1)
        ).join(', ') || 'N/A'}`

      case 'time_range':
        if (condition.timeRange) {
          return `Time: ${condition.timeRange.startTime} - ${condition.timeRange.endTime}`
        }
        return 'Time range not specified'

      case 'min_quantity':
        return `Minimum quantity: ${condition.minQuantity || 'N/A'}`

      case 'max_uses_per_order':
        return `Max uses per order: ${condition.maxUsesPerOrder || 'N/A'}`

      case 'min_order_value':
        return `Minimum order value: ${condition.minOrderValue?.toFixed(2) || '0.00'}`

      case 'payment_method':
        return `Payment methods: ${condition.paymentMethods?.map(method =>
          method.toUpperCase()
        ).join(', ') || 'N/A'}`

      case 'first_order':
        return condition.isFirstOrder ? 'First order only' : 'Any order'

      default:
        return condition.value || 'No additional details'
    }
  } catch (error) {
    console.error('❌ Error getting condition display text:', error)
    return 'Invalid condition'
  }
}

/**
 * Get condition type text
 * @param {string} type - Condition type
 * @returns {string} - Formatted type text
 */
export const getConditionTypeText = (type) => {
  const types = {
    'days_active': 'Days Active',
    'time_range': 'Time Range',
    'min_quantity': 'Minimum Quantity',
    'max_uses_per_order': 'Max Uses Per Order',
    'min_order_value': 'Minimum Order Value',
    'payment_method': 'Payment Method',
    'first_order': 'First Order'
  }
  return types[type] || type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'
}

/**
 * Get target audience name from object or ID
 * @param {Object|string} targetAudience - Target audience object or ID
 * @returns {string} - Audience name
 */
export const getTargetAudienceName = (targetAudience) => {
  if (!targetAudience) return 'N/A'
  
  if (typeof targetAudience === 'object' && targetAudience.name) {
    return targetAudience.name
  }
  
  if (typeof targetAudience === 'string') {
    return 'Customer Group' // You might want to fetch the actual name
  }
  
  return 'Unknown'
}

/**
 * Validate promotion form data
 * @param {Object} formData - Form data to validate
 * @returns {Object} - Validation result with errors
 */
export const validatePromotionForm = (formData) => {
  const errors = {}

  // Required field validation
  if (!formData.name?.trim()) {
    errors.name = 'Promotion name is required'
  }

  if (!formData.promotionType) {
    errors.promotionType = 'Promotion type is required'
  }

  if (!formData.discountType) {
    errors.discountType = 'Discount type is required'
  }

  if (!formData.discountValue || formData.discountValue <= 0) {
    errors.discountValue = 'Discount value must be greater than 0'
  }

  if (formData.discountType === 'percentage' && formData.discountValue > 100) {
    errors.discountValue = 'Percentage discount cannot exceed 100%'
  }

  if (!formData.targetAudience) {
    errors.targetAudience = 'Target audience is required'
  }

  if (!formData.startDate) {
    errors.startDate = 'Start date is required'
  }

  if (!formData.endDate) {
    errors.endDate = 'End date is required'
  }

  // Date validation
  if (formData.startDate && formData.endDate) {
    const startDate = new Date(formData.startDate)
    const endDate = new Date(formData.endDate)
    
    if (endDate <= startDate) {
      errors.endDate = 'End date must be after start date'
    }

    if (startDate < new Date().setHours(0, 0, 0, 0)) {
      errors.startDate = 'Start date cannot be in the past'
    }
  }

  // Product validation for product-based promotions
  if (formData.promotionType === 'product_based') {
    if (!formData.products || formData.products.length === 0) {
      errors.products = 'At least one product is required for product-based promotions'
    }
  }

  // Condition validation
  if (formData.conditions && Array.isArray(formData.conditions)) {
    formData.conditions.forEach((condition, index) => {
      if (condition && condition.type) {
        const conditionErrors = validateCondition(condition)
        if (Object.keys(conditionErrors).length > 0) {
          errors[`condition_${index}`] = conditionErrors
        }
      }
    })
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

/**
 * Validate individual condition
 * @param {Object} condition - Condition to validate
 * @returns {Object} - Validation errors for the condition
 */
export const validateCondition = (condition) => {
  const errors = {}

  switch (condition.type) {
    case 'days_active':
      if (!condition.daysActive || !Array.isArray(condition.daysActive) || condition.daysActive.length === 0) {
        errors.daysActive = 'At least one day must be selected'
      }
      break

    case 'time_range':
      if (!condition.timeRange?.startTime || !condition.timeRange?.endTime) {
        errors.timeRange = 'Both start time and end time are required'
      }
      break

    case 'min_quantity':
      if (!condition.minQuantity || condition.minQuantity <= 0) {
        errors.minQuantity = 'Minimum quantity must be greater than 0'
      }
      break

    case 'max_uses_per_order':
      if (!condition.maxUsesPerOrder || condition.maxUsesPerOrder <= 0) {
        errors.maxUsesPerOrder = 'Max uses per order must be greater than 0'
      }
      break

    case 'min_order_value':
      if (!condition.minOrderValue || condition.minOrderValue <= 0) {
        errors.minOrderValue = 'Minimum order value must be greater than 0'
      }
      break

    case 'payment_method':
      if (!condition.paymentMethods || !Array.isArray(condition.paymentMethods) || condition.paymentMethods.length === 0) {
        errors.paymentMethods = 'At least one payment method must be selected'
      }
      break
  }

  return errors
}

/**
 * Get default condition structure for a given type
 * @param {string} type - Condition type
 * @returns {Object} - Default condition object
 */
export const getDefaultCondition = (type) => {
  const defaults = {
    'days_active': {
      type: 'days_active',
      daysActive: []
    },
    'time_range': {
      type: 'time_range',
      timeRange: {
        startTime: '09:00',
        endTime: '17:00'
      }
    },
    'min_quantity': {
      type: 'min_quantity',
      minQuantity: 1
    },
    'max_uses_per_order': {
      type: 'max_uses_per_order',
      maxUsesPerOrder: 1
    },
    'min_order_value': {
      type: 'min_order_value',
      minOrderValue: 0
    },
    'payment_method': {
      type: 'payment_method',
      paymentMethods: []
    },
    'first_order': {
      type: 'first_order',
      isFirstOrder: true
    }
  }

  return defaults[type] || { type }
}

/**
 * Get available condition types for a promotion type
 * @param {string} promotionType - Type of promotion
 * @returns {Array} - Available condition types
 */
export const getAvailableConditions = (promotionType) => {
  const allConditions = [
    { value: 'days_active', label: 'Days Active', description: 'Specify which days the promotion is active' },
    { value: 'time_range', label: 'Time Range', description: 'Set specific hours when promotion is active' },
    { value: 'min_quantity', label: 'Minimum Quantity', description: 'Minimum quantity required to activate promotion' },
    { value: 'max_uses_per_order', label: 'Max Uses Per Order', description: 'Maximum times promotion can be used in single order' },
    { value: 'min_order_value', label: 'Minimum Order Value', description: 'Minimum order total required' },
    { value: 'payment_method', label: 'Payment Method', description: 'Restrict to specific payment methods' },
    { value: 'first_order', label: 'First Order', description: 'Apply only to customer\'s first order' }
  ]

  // Return different conditions based on promotion type
  switch (promotionType) {
    case 'product_based':
      return allConditions.filter(c => 
        ['days_active', 'time_range', 'min_quantity', 'max_uses_per_order'].includes(c.value)
      )
    
    case 'order_based':
      return allConditions.filter(c => 
        ['days_active', 'time_range', 'min_order_value', 'max_uses_per_order'].includes(c.value)
      )
    
    case 'customer_based':
      return allConditions.filter(c => 
        ['days_active', 'time_range', 'min_order_value', 'max_uses_per_order', 'first_order'].includes(c.value)
      )
    
    case 'quantity_based':
      return allConditions.filter(c => 
        ['days_active', 'time_range', 'min_quantity', 'min_order_value', 'max_uses_per_order'].includes(c.value)
      )
    
    default:
      return allConditions
  }
}

/**
 * Check if promotion is currently active based on dates and time
 * @param {Object} promotion - Promotion object
 * @returns {boolean} - Whether promotion is currently active
 */
export const isPromotionCurrentlyActive = (promotion) => {
  if (!promotion.isActive) return false
  
  const now = new Date()
  const startDate = new Date(promotion.startDate)
  const endDate = new Date(promotion.endDate)
  
  // Check if current date is within promotion period
  if (now < startDate || now > endDate) return false
  
  // Check day conditions
  const dayCondition = promotion.conditions?.find(c => c.type === 'days_active')
  if (dayCondition && dayCondition.daysActive) {
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
    if (!dayCondition.daysActive.includes(currentDay)) return false
  }
  
  // Check time conditions
  const timeCondition = promotion.conditions?.find(c => c.type === 'time_range')
  if (timeCondition && timeCondition.timeRange) {
    const currentTime = now.toTimeString().slice(0, 5) // HH:MM format
    if (currentTime < timeCondition.timeRange.startTime || currentTime > timeCondition.timeRange.endTime) {
      return false
    }
  }
  
  return true
}

/**
 * Calculate promotion savings for display
 * @param {Object} promotion - Promotion object
 * @param {number} originalPrice - Original price
 * @returns {Object} - Savings calculation
 */
export const calculatePromotionSavings = (promotion, originalPrice = 100) => {
  if (!promotion || !originalPrice) {
    return { savings: 0, finalPrice: originalPrice, savingsText: 'N/A' }
  }
  
  let savings = 0
  let finalPrice = originalPrice
  
  if (promotion.discountType === 'fixed') {
    savings = Math.min(promotion.discountValue, originalPrice)
    finalPrice = Math.max(0, originalPrice - promotion.discountValue)
  } else if (promotion.discountType === 'percentage') {
    savings = (originalPrice * promotion.discountValue) / 100
    finalPrice = originalPrice - savings
  }
  
  const savingsText = promotion.discountType === 'fixed' 
    ? `${savings.toFixed(2)} off`
    : `${promotion.discountValue}% off`
  
  return {
    savings: parseFloat(savings.toFixed(2)),
    finalPrice: parseFloat(finalPrice.toFixed(2)),
    savingsText
  }
}